import { nextTick } from 'vue'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import type { Company } from '@cplug/frontend-shared-domains/domain/company'

const AUTH_STORE_PERSIST_KEY = 'erp'

interface PersistedAuthState {
  companies?: Company[]
  token?: string
  refreshToken?: string
}

function readPersistedAuthState (): PersistedAuthState | undefined {
  if (typeof localStorage === 'undefined') {
    return undefined
  }

  try {
    const raw = localStorage.getItem(AUTH_STORE_PERSIST_KEY)
    if (raw == null) {
      return undefined
    }

    return JSON.parse(raw) as PersistedAuthState
  } catch {
    return undefined
  }
}

export function ensurePersistedCompaniesLoaded (): void {
  const authStore = useAuthenticationStore()
  if (authStore.companies.length > 0) {
    return
  }

  const persisted = readPersistedAuthState()
  if ((persisted?.companies?.length ?? 0) > 0) {
    authStore.setCompanies(persisted?.companies ?? [])
  }
}

/**
 * Recupera a sessão bearer de quem já estava logado antes do ambiente virar cookie mode.
 *
 * A hidratação do persist já faz isso, e esta função é a rede de segurança contra o timing
 * dela — o mesmo motivo pelo qual `ensurePersistedCompaniesLoaded` relê o blob na mão.
 * Perder essa credencial significa deslogar o cliente, então vale a redundância.
 */
export function ensurePersistedBearerSessionLoaded (): void {
  const authStore = useAuthenticationStore()
  if (authStore.logged) {
    return
  }

  const persisted = readPersistedAuthState()
  authStore.adoptPersistedBearerSession(persisted?.token ?? '', persisted?.refreshToken ?? '')
}

export async function waitForAuthStoreHydration (): Promise<void> {
  await nextTick()
  ensurePersistedCompaniesLoaded()
  ensurePersistedBearerSessionLoaded()
  await nextTick()
}
